import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Box, Layers, Loader2, RotateCcw, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TopNav } from "@/components/app/TopNav";
import { UploadDropzone } from "@/components/app/UploadDropzone";
import { ReliefControls } from "@/components/app/ReliefControls";
import { AiControls, AiJobProgress, AiUnavailable } from "@/components/app/AiControls";
import { Viewport } from "@/components/app/Viewport";
import { ViewToolbar } from "@/components/app/ViewToolbar";
import { InfoPanel } from "@/components/app/InfoPanel";
import { HelpDialog, PrivacyDialog, ProjectsSheet } from "@/components/app/Dialogs";
import { Section } from "@/components/app/Fields";
import * as api from "@/lib/api";
import { ACCEPT, DEFAULT_PARAMS, SAMPLES, fileFromSample } from "@/lib/constants";

const AI_DEFAULT = { target_size_mm: 80, rotation_x: 0, rotation_z: 0 };

export default function Workspace() {
  const [search] = useSearchParams();
  const viewer = useRef(null);
  const [config, setConfig] = useState(null);
  const [upload, setUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mode, setMode] = useState("relief");
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [lockAspect, setLockAspect] = useState(true);
  const [aiParams, setAiParams] = useState(AI_DEFAULT);
  const [model, setModel] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [view, setView] = useState("perspective");
  const [wireframe, setWireframe] = useState(false);
  const [bg, setBg] = useState("#141415");
  const [downloading, setDownloading] = useState(false);
  const [dialogs, setDialogs] = useState({ help: false, privacy: false, projects: false });
  const [projects, setProjects] = useState(api.listProjects());
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const stopPolling = useCallback(() => {
    clearTimeout(pollRef.current);
    clearInterval(tickRef.current);
  }, []);

  const refreshConfig = useCallback(() => api.getConfig().then(setConfig).catch(() => {}), []);
  useEffect(() => {
    refreshConfig();
    return () => stopPolling();
  }, [refreshConfig, stopPolling]);

  const limits = config?.limits;
  const aspect = upload ? upload.height / upload.width : null;

  const remember = (m, up) => {
    api.saveProject({ model_id: m.id, upload_id: m.upload_id, name: up?.name || "model", width: up?.width, height: up?.height, kind: m.kind, triangles: m.stats.triangles, created_at: m.created_at });
    setProjects(api.listProjects());
  };

  const runRelief = async (up = upload, p = params) => {
    if (!up) return;
    setGenerating(true);
    setError(null);
    try {
      const m = await api.createRelief(up.id, p);
      setModel(m);
      setDirty(false);
      remember(m, up);
      toast.success(`Relief generated · ${m.stats.triangles.toLocaleString()} triangles`);
    } catch (e) {
      const msg = api.errMsg(e, "Relief conversion failed.");
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
      refreshConfig();
    }
  };

  const handleFile = async (file, presetParams) => {
    if (!ACCEPT.includes(file.type)) {
      toast.error("Unsupported format. Please upload PNG, JPG/JPEG or WEBP.");
      return;
    }
    const maxMb = limits?.max_upload_mb ?? 10;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`File is too large. Maximum size is ${maxMb} MB.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      const up = await api.uploadImage(file, setProgress);
      const withPreview = { ...up, previewUrl: URL.createObjectURL(file) };
      setUpload(withPreview);
      setModel(null);
      setJob(null);
      const p = presetParams ? { ...DEFAULT_PARAMS, ...presetParams } : { ...params, height_mm: null };
      setParams(p);
      if (mode === "relief") runRelief(withPreview, p);
    } catch (e) {
      toast.error(api.errMsg(e, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const handleSample = async (s) => {
    try {
      handleFile(await fileFromSample(s), s.params);
    } catch {
      toast.error("Could not load the sample image.");
    }
  };

  useEffect(() => {
    const key = search.get("sample");
    const s = SAMPLES.find((x) => x.key === key);
    if (s) handleSample(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setP = (patch) => {
    setParams((p) => ({ ...p, ...patch }));
    setDirty(true);
  };

  const startAi = async () => {
    if (!upload) return;
    stopPolling();
    setError(null);
    setModel(null);
    try {
      const j = await api.createAiJob(upload.id, aiParams.target_size_mm);
      setJob(j);
      const started = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
      const poll = async () => {
        let cur;
        try {
          cur = await api.getAiJob(j.id);
        } catch {
          pollRef.current = setTimeout(poll, 2000);
          return;
        }
        setJob(cur);
        if (cur.status === "running") {
          pollRef.current = setTimeout(poll, 2000);
          return;
        }
        clearInterval(tickRef.current);
        try {
          if (cur.status === "completed") {
            let m = await api.getModel(cur.model_id);
            if (aiParams.rotation_x || aiParams.rotation_z) m = await api.transformModel(m.id, aiParams);
            setModel(m);
            remember(m, upload);
            toast.success("AI 3D model ready");
            refreshConfig();
          } else {
            toast.error(cur.error || "AI conversion failed");
          }
        } catch (e) {
          toast.error(api.errMsg(e, "Could not load the AI model."));
        }
      };
      pollRef.current = setTimeout(poll, 2000);
    } catch (e) {
      const msg = api.errMsg(e, "Could not start AI conversion.");
      setError(msg);
      toast.error(msg);
    }
  };

  const applyAiTransform = async () => {
    setGenerating(true);
    try {
      const m = await api.transformModel(model.id, aiParams);
      setModel(m);
      remember(m, upload);
    } catch (e) {
      toast.error(api.errMsg(e, "Transform failed."));
    } finally {
      setGenerating(false);
    }
  };

  const download = async (fmt) => {
    setDownloading(true);
    try {
      const res = await fetch(api.downloadUrl(model.id, fmt));
      if (!res.ok) throw new Error(res.status === 404 ? "Model expired. Please regenerate it." : "Download failed");
      const buf = await res.arrayBuffer();
      if (fmt === "stl") {
        const n = new DataView(buf).getUint32(80, true);
        if (buf.byteLength !== 84 + 50 * n || n !== model.stats.triangles) throw new Error("Downloaded STL failed verification. Please retry.");
      } else if (buf.byteLength < 64) throw new Error("Downloaded file is empty. Please retry.");
      const url = URL.createObjectURL(new Blob([buf], { type: res.headers.get("content-type") || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(upload?.name || "image2stl").slice(0, 40)}-${model.kind}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`${fmt.toUpperCase()} downloaded and verified (${(buf.byteLength / 1024 / 1024).toFixed(2)} MB)`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const newProject = () => {
    stopPolling();
    setUpload(null);
    setModel(null);
    setJob(null);
    setError(null);
    setParams(DEFAULT_PARAMS);
    setAiParams(AI_DEFAULT);
    setDirty(false);
  };

  const openProject = async (p) => {
    try {
      const m = await api.getModel(p.model_id);
      setModel(m);
      setUpload({ id: m.upload_id, name: p.name, previewUrl: api.uploadImageUrl(m.upload_id), width: p.width ?? "?", height: p.height ?? "?", format: "png" });
      setMode(m.kind === "ai" ? "ai" : "relief");
      if (m.kind === "relief") setParams({ ...DEFAULT_PARAMS, ...m.params });
      setDialogs((d) => ({ ...d, projects: false }));
    } catch {
      toast.error("This project has expired on the server (24 h retention) and was removed.");
      api.removeProject(p.model_id);
      setProjects(api.listProjects());
    }
  };

  const busy = generating || uploading || job?.status === "running";
  const aiReady = config?.ai_available;

  return (
    <div className="h-screen flex flex-col bg-[#0d0d0e] text-white overflow-hidden">
      <TopNav onNew={newProject} onProjects={() => setDialogs((d) => ({ ...d, projects: true }))} onHelp={() => setDialogs((d) => ({ ...d, help: true }))} />

      <main className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3 p-3 overflow-y-auto lg:overflow-hidden">
        {/* Left sidebar */}
        <aside className="panel order-2 lg:order-1 lg:w-80 shrink-0 flex flex-col lg:min-h-0" data-testid="left-sidebar">
          <div className="p-5 space-y-8 lg:overflow-y-auto thin-scroll flex-1">
            <Section title="1 · Image">
              <UploadDropzone upload={upload} uploading={uploading} progress={progress} maxMb={limits?.max_upload_mb ?? 10} onFile={(f) => handleFile(f)} onSample={handleSample} />
            </Section>
            <Section title="2 · Mode">
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Conversion mode">
                {[
                  { v: "relief", icon: Layers, t: "Relief", d: "Free · local" },
                  { v: "ai", icon: Sparkles, t: "AI Full 3D", d: aiReady ? "Cloud AI" : "Needs API key" },
                ].map(({ v, icon: Icon, t, d }) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={mode === v}
                    data-testid={`mode-${v}-btn`}
                    onClick={() => setMode(v)}
                    className={`text-left p-3 rounded-xl border transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a00] ${
                      mode === v ? "border-[#ff5a00] bg-[#ff5a00]/10" : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <Icon size={16} className={mode === v ? "text-[#ff5a00]" : "text-zinc-400"} />
                    <p className="text-sm font-medium mt-2">{t}</p>
                    <p className="text-[11px] text-zinc-500">{d}</p>
                  </button>
                ))}
              </div>
            </Section>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="eyebrow">3 · Settings</h3>
                {mode === "relief" && (
                  <button type="button" data-testid="reset-settings-btn" onClick={() => { setParams(DEFAULT_PARAMS); setDirty(true); }} className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1 transition-colors">
                    <RotateCcw size={11} /> Reset all
                  </button>
                )}
              </div>
              {mode === "relief" ? (
                <ReliefControls params={params} set={setP} aspect={aspect} lockAspect={lockAspect} setLockAspect={setLockAspect} />
              ) : aiReady ? (
                <AiControls provider={config.ai_provider} aiParams={aiParams} setAiParams={setAiParams} limits={limits} />
              ) : (
                <AiUnavailable onSwitch={() => setMode("relief")} />
              )}
            </div>
            {mode === "ai" && <AiJobProgress job={job} elapsed={elapsed} onRetry={startAi} />}
          </div>
          <div className="p-4 border-t border-white/5">
            {mode === "relief" ? (
              <Button data-testid="generate-btn" disabled={!upload || busy} onClick={() => runRelief()} className="w-full h-11 bg-[#ff5a00] hover:bg-[#ff7a33] text-white font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
                {generating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 className="mr-2" size={16} />}
                {model && dirty ? "Update 3D model" : "Generate 3D model"}
              </Button>
            ) : model?.kind === "ai" ? (
              <div className="grid grid-cols-2 gap-2">
                <Button variant="secondary" data-testid="ai-apply-transform-btn" disabled={busy} onClick={applyAiTransform}>Apply size/rotation</Button>
                <Button data-testid="ai-generate-btn" disabled={!upload || busy || !aiReady} onClick={startAi} className="bg-[#ff5a00] hover:bg-[#ff7a33] text-white">New AI run</Button>
              </div>
            ) : (
              <Button data-testid="ai-generate-btn" disabled={!upload || busy || !aiReady} onClick={startAi} className="w-full h-11 bg-[#ff5a00] hover:bg-[#ff7a33] text-white font-semibold">
                {job?.status === "running" ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles className="mr-2" size={16} />}
                Generate with AI
              </Button>
            )}
            {!upload && <p className="text-[11px] text-zinc-500 mt-2 text-center">Upload an image to begin</p>}
          </div>
        </aside>

        {/* Center viewport */}
        <section className="order-1 lg:order-2 relative flex-1 min-h-[55vh] lg:min-h-0 rounded-2xl overflow-hidden border border-white/5 grain" data-testid="viewport">
          <Viewport ref={viewer} url={model ? api.meshUrl(model.id) : null} wireframe={wireframe} background={bg} onError={(m) => toast.error(m)} />
          {model && <ViewToolbar viewer={viewer} view={view} setView={setView} wireframe={wireframe} setWireframe={setWireframe} bg={bg} setBg={setBg} />}
          {model && (
            <div className="hidden md:block absolute top-4 left-4 z-10 px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-xl border border-white/10 font-mono text-[11px] text-zinc-300" data-testid="viewport-model-badge">
              {model.kind === "ai" ? "AI MODEL" : "RELIEF"} · {model.stats.size_mm.join(" × ")} mm
            </div>
          )}
          {!model && !busy && (
            <div className="absolute inset-0 flex items-end lg:items-center p-8 lg:p-14 pointer-events-none" data-testid="viewport-empty-state">
              <div className="max-w-md rise">
                <Box className="text-[#ff5a00] mb-4" size={28} />
                <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">{error ? "Conversion didn't work" : "Your model appears here"}</h2>
                <p className="text-zinc-400 mt-3 text-sm leading-relaxed" data-testid="viewport-error">{error || "Upload an image or pick a sample on the left. Relief mode turns it into a printable 3D mesh in seconds."}</p>
              </div>
            </div>
          )}
          {busy && (
            <div className="absolute inset-0 z-20 bg-[#0d0d0e]/70 backdrop-blur-sm flex items-center justify-center" data-testid="viewport-loading">
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#161617] px-6 py-5 w-72">
                <div className="absolute inset-x-0 h-1/2 bg-gradient-to-b from-transparent via-[#ff5a00]/10 to-transparent scanline" />
                <div className="relative flex items-center gap-3">
                  <Loader2 className="animate-spin text-[#ff5a00]" size={18} />
                  <div>
                    <p className="text-sm font-medium">{uploading ? "Uploading image" : job?.status === "running" ? job.stage_label : "Building mesh"}</p>
                    <p className="text-[11px] text-zinc-500 font-mono">{job?.status === "running" ? `${elapsed}s elapsed` : uploading ? `${progress}%` : "Generating geometry on server"}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Right sidebar */}
        <aside className="panel order-3 lg:w-72 shrink-0 lg:min-h-0 lg:overflow-y-auto thin-scroll p-5" data-testid="right-sidebar">
          <InfoPanel model={model} onDownload={download} downloading={downloading} />
          <button type="button" data-testid="privacy-link" onClick={() => setDialogs((d) => ({ ...d, privacy: true }))} className="mt-8 text-[11px] text-zinc-500 hover:text-white underline underline-offset-4 transition-colors">
            Privacy notice
          </button>
        </aside>
      </main>

      <HelpDialog open={dialogs.help} onOpenChange={(v) => setDialogs((d) => ({ ...d, help: v }))} limits={limits} />
      <PrivacyDialog open={dialogs.privacy} onOpenChange={(v) => setDialogs((d) => ({ ...d, privacy: v }))} hours={limits?.retention_hours} />
      <ProjectsSheet
        open={dialogs.projects}
        onOpenChange={(v) => setDialogs((d) => ({ ...d, projects: v }))}
        projects={projects}
        onOpen={openProject}
        onRemove={(id) => { api.removeProject(id); setProjects(api.listProjects()); }}
      />
    </div>
  );
}
